/**
 * /nature — SocialPulse Global Weather & Nature Hub.
 *
 * Two primary sections (Weather Almanac | Nature Museum), each with its own
 * inner sub-tabs. User selects a category when uploading an observation; the
 * feed can be filtered by category chips.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import TopicNewsStrip from "@/components/TopicNewsStrip";
import {
  Cloud, Sun, CloudRain, MapPin, Camera, BookOpen,
  Trophy, Bell, Wind, Droplets, Eye as EyeIcon, Upload,
  Sparkles, Loader2, Trash2, Search, User, Compass, Leaf,
} from "lucide-react";

/* -------- Section + sub-tab config -------- */
const SECTIONS = [
  { v: "weather", label: "Weather Almanac", Icon: Cloud, blurb: "Live conditions, 5-day forecast, severe-weather alerts." },
  { v: "nature",  label: "Nature Museum",   Icon: Leaf,  blurb: "Community observations, map, personal journal & leaderboard." },
];

const WEATHER_TABS = [
  { v: "current",  label: "Current",   Icon: Cloud },
  { v: "forecast", label: "Forecast",  Icon: Sun },
  { v: "alerts",   label: "Alerts",    Icon: Bell },
];
const NATURE_TABS = [
  { v: "observations", label: "Observations", Icon: Camera },
  { v: "map",          label: "Map",          Icon: MapPin },
  { v: "journal",      label: "My Journal",   Icon: BookOpen },
  { v: "leaderboard",  label: "Leaderboard",  Icon: Trophy },
];

export default function Nature() {
  const [params, setParams] = useSearchParams();
  const initialSection = SECTIONS.find((s) => s.v === params.get("section"))?.v || "nature";
  const [section, setSection] = useState(initialSection);
  const initialTab = params.get("tab") ||
    (initialSection === "weather" ? "current" : "observations");
  const [tab, setTab] = useState(initialTab);

  const [coords, setCoords] = useState(null);
  const [geoError, setGeoError] = useState(null);

  useEffect(() => {
    if (!coords) {
      if (!("geolocation" in navigator)) {
        setGeoError("Your browser can't share location. Pick one manually.");
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => setGeoError("We couldn't detect your location. Falling back to New York."),
        { timeout: 8000 }
      );
    }
  }, [coords]);

  const effectiveCoords = coords || { lat: 40.7128, lng: -74.006 };  // NYC fallback

  const setActiveSection = (v) => {
    setSection(v);
    const first = v === "weather" ? "current" : "observations";
    setTab(first);
    setParams({ section: v, tab: first });
  };
  const setActiveTab = (v) => { setTab(v); setParams({ section, tab: v }); };

  const tabs = section === "weather" ? WEATHER_TABS : NATURE_TABS;

  return (
    <div className="max-w-6xl mx-auto px-6 py-10" data-testid="nature-page">
      {/* Latest Environment / Nature news */}
      <TopicNewsStrip topic="Nature & Environment" category="Environment" testId="nature-news-strip" />
      <div className="overline">SOCIALPULSE · GLOBAL WEATHER & NATURE HUB</div>
      <h1 className="font-display text-4xl sm:text-5xl mt-2">Watch the world. Save what you see.</h1>
      <p className="mt-3 max-w-2xl" style={{ color: "var(--wofy-text-soft)" }}>
        Track daily weather where you are, share nature observations with GPS precision, and help build a living record of our planet.{" "}
        <Link to="/nature/discover" data-testid="nature-discover-link" className="underline" style={{ color: "var(--wofy-primary)" }}>
          Explore the citizen-science dashboard →
        </Link>
      </p>

      {geoError && (
        <p className="mt-3 text-xs px-3 py-2 rounded-lg" style={{ background: "var(--wofy-surface)", color: "var(--wofy-text-soft)" }}>
          {geoError}
        </p>
      )}

      {/* Section switcher (Weather Almanac vs Nature Museum) */}
      <div className="mt-8 grid sm:grid-cols-2 gap-3" data-testid="nature-section-switch">
        {SECTIONS.map(({ v, label, Icon, blurb }) => {
          const active = section === v;
          return (
            <button
              key={v}
              data-testid={`nature-section-${v}`}
              onClick={() => setActiveSection(v)}
              className="text-left rounded-xl p-4 border-2 transition"
              style={{
                borderColor: active ? "var(--wofy-primary)" : "var(--wofy-border)",
                background: active ? "var(--wofy-surface)" : "white",
              }}
            >
              <div className="flex items-center gap-2">
                <Icon className="w-5 h-5" style={{ color: "var(--wofy-primary)" }} />
                <div className="font-display text-xl">{label}</div>
              </div>
              <div className="text-xs mt-1" style={{ color: "var(--wofy-text-soft)" }}>{blurb}</div>
            </button>
          );
        })}
      </div>

      {/* Inner sub-tabs */}
      <div className="mt-6 border-b flex gap-1 overflow-x-auto" style={{ borderColor: "var(--wofy-border)" }}>
        {tabs.map(({ v, label, Icon }) => (
          <button
            key={v}
            data-testid={`nature-tab-${v}`}
            onClick={() => setActiveTab(v)}
            className="px-3 py-2 text-sm border-b-2 -mb-px inline-flex items-center gap-1.5 whitespace-nowrap"
            style={{ borderColor: tab === v ? "var(--wofy-primary)" : "transparent", color: tab === v ? "var(--wofy-primary)" : "var(--wofy-text-soft)" }}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {/* Weather Almanac */}
        {section === "weather" && tab === "current" && <WeatherCurrent coords={effectiveCoords} />}
        {section === "weather" && tab === "forecast" && <WeatherForecast coords={effectiveCoords} />}
        {section === "weather" && tab === "alerts"   && <AlertsTab coords={effectiveCoords} />}

        {/* Nature Museum */}
        {section === "nature" && tab === "observations" && <ObservationsTab coords={effectiveCoords} />}
        {section === "nature" && tab === "map"          && <MapTab coords={effectiveCoords} />}
        {section === "nature" && tab === "journal"      && <JournalTab />}
        {section === "nature" && tab === "leaderboard"  && <LeaderboardTab />}
      </div>
    </div>
  );
}


/* -------------------- Weather Almanac -------------------- */
function useWeather(coords) {
  const [cur, setCur] = useState(null);
  const [fc, setFc] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let alive = true;
    setErr(null);
    Promise.all([
      http.get(`/nature/weather/current?lat=${coords.lat}&lng=${coords.lng}`),
      http.get(`/nature/weather/forecast?lat=${coords.lat}&lng=${coords.lng}&days=5`),
    ])
      .then(([c, f]) => { if (alive) { setCur(c.data); setFc(f.data); } })
      .catch(() => { if (alive) setErr("Could not load weather. Try another location."); });
    return () => { alive = false; };
  }, [coords.lat, coords.lng]);
  return { cur, fc, err };
}

function WeatherCurrent({ coords }) {
  const { cur, err } = useWeather(coords);
  if (err) return <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>{err}</p>;
  if (!cur) return <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>Loading weather…</p>;
  const c = cur.current;
  return (
    <div data-testid="weather-current">
      <div className="wofy-card p-8 flex items-start gap-6 flex-wrap">
        {c.icon && <img src={`https:${c.icon}`} alt="" className="w-24 h-24" data-testid="weather-icon" />}
        <div className="flex-1 min-w-[220px]">
          <div className="overline">{cur.location.name}{cur.location.country ? `, ${cur.location.country}` : ""}</div>
          <div className="font-display text-6xl mt-1" data-testid="weather-temp">
            {Math.round(c.temp_c)}°<span className="text-2xl align-top" style={{ color: "var(--wofy-text-soft)" }}>C</span>
          </div>
          <div className="text-lg font-medium mt-1">{c.condition}</div>
          <div className="text-xs font-mono mt-1" style={{ color: "var(--wofy-text-soft)" }}>
            Feels like {Math.round(c.feels_like_c)}° · updated {c.updated}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <StatChip Icon={Wind} label="Wind" value={`${c.wind_kph} kph ${c.wind_dir}`} />
          <StatChip Icon={Droplets} label="Humidity" value={`${c.humidity}%`} />
          <StatChip Icon={EyeIcon} label="Visibility" value={`${c.vis_km} km`} />
          <StatChip Icon={Sun} label="UV" value={c.uv} />
        </div>
      </div>
    </div>
  );
}

function WeatherForecast({ coords }) {
  const { fc, err } = useWeather(coords);
  if (err) return <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>{err}</p>;
  if (!fc) return <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>Loading forecast…</p>;
  return (
    <div data-testid="weather-forecast">
      <div className="overline mb-3">5-DAY FORECAST</div>
      <div className="grid sm:grid-cols-5 gap-3">
        {fc.days.map((d) => (
          <div key={d.date} className="wofy-card p-4 text-center" data-testid={`forecast-day-${d.date}`}>
            <div className="text-xs font-mono uppercase" style={{ color: "var(--wofy-text-soft)" }}>
              {new Date(d.date).toLocaleDateString(undefined, { weekday: "short" })}
            </div>
            {d.icon && <img src={`https:${d.icon}`} alt="" className="w-12 h-12 mx-auto my-1" />}
            <div className="text-xs">{d.condition}</div>
            <div className="mt-1 text-sm">
              <b>{Math.round(d.max_c)}°</b>
              <span className="mx-1" style={{ color: "var(--wofy-text-soft)" }}>/</span>
              {Math.round(d.min_c)}°
            </div>
            <div className="mt-1 text-[10px] font-mono flex items-center justify-center gap-1" style={{ color: "var(--wofy-text-soft)" }}>
              <CloudRain className="w-3 h-3" /> {d.chance_rain || 0}%
            </div>
            <div className="mt-1 text-[10px] font-mono" style={{ color: "var(--wofy-text-soft)" }}>
              Sunrise {d.sunrise} · Sunset {d.sunset}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function StatChip({ Icon, label, value }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: "var(--wofy-surface)" }}>
      <Icon className="w-4 h-4 opacity-60" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase font-mono" style={{ color: "var(--wofy-text-soft)" }}>{label}</div>
        <div className="font-semibold text-sm truncate">{value}</div>
      </div>
    </div>
  );
}


/* -------------------- Category catalog helper -------------------- */
function useCategories() {
  const [cats, setCats] = useState([]);
  useEffect(() => {
    http.get("/nature/categories").then((r) => setCats(r.data.categories || [])).catch(() => setCats([]));
  }, []);
  const byKey = useMemo(() => Object.fromEntries(cats.map((c) => [c.key, c])), [cats]);
  return { cats, byKey };
}


/* -------------------- Nature Museum · Observations -------------------- */
function ObservationsTab({ coords }) {
  const { user } = useAuth();
  const { cats, byKey } = useCategories();
  const [items, setItems] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [species, setSpecies] = useState("");
  const [scope, setScope] = useState("all");
  const [category, setCategory] = useState("");

  const load = useCallback(() => {
    const params = new URLSearchParams({ scope, limit: "48" });
    if (species) params.set("species", species);
    if (category) params.set("category", category);
    if (scope === "near") { params.set("lat", coords.lat); params.set("lng", coords.lng); }
    http.get(`/nature/observations?${params.toString()}`)
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, [scope, species, category, coords.lat, coords.lng]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="observations-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex gap-2 text-xs flex-wrap">
          {[["all", "Community"], ["mine", "Mine"], ["near", "Nearby"]].map(([v, label]) => (
            <button
              key={v}
              data-testid={`obs-scope-${v}`}
              onClick={() => setScope(v)}
              disabled={v === "mine" && !user}
              className={`px-3 py-1.5 rounded-full border transition ${scope === v ? "bg-[var(--wofy-primary)] text-white border-transparent" : ""}`}
              style={scope !== v ? { borderColor: "var(--wofy-border)" } : undefined}
            >
              {label}
            </button>
          ))}
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 opacity-50" />
            <input
              data-testid="obs-species-filter"
              value={species}
              onChange={(e) => setSpecies(e.target.value)}
              placeholder="Filter by species…"
              className="pl-8 pr-3 py-1.5 rounded-full border text-xs"
              style={{ borderColor: "var(--wofy-border)" }}
            />
          </div>
        </div>
        {user && (
          <button
            data-testid="obs-upload-btn"
            onClick={() => setShowUpload(true)}
            className="wofy-btn-primary text-sm"
          >
            <Upload className="w-4 h-4" /> Share an observation
          </button>
        )}
      </div>

      {/* Category filter chips */}
      {cats.length > 0 && (
        <div className="mb-5 -mx-1 flex flex-wrap gap-1.5" data-testid="obs-category-chips">
          <button
            data-testid="obs-category-chip-all"
            onClick={() => setCategory("")}
            className={`text-[11px] px-3 py-1 rounded-full border ${category === "" ? "bg-[var(--wofy-primary)] text-white border-transparent" : ""}`}
            style={category !== "" ? { borderColor: "var(--wofy-border)" } : undefined}
          >
            All categories
          </button>
          {cats.map((c) => (
            <button
              key={c.key}
              data-testid={`obs-category-chip-${c.key}`}
              onClick={() => setCategory(category === c.key ? "" : c.key)}
              className={`text-[11px] px-3 py-1 rounded-full border ${category === c.key ? "bg-[var(--wofy-primary)] text-white border-transparent" : ""}`}
              style={category !== c.key ? { borderColor: "var(--wofy-border)" } : undefined}
            >
              {c.label}
            </button>
          ))}
        </div>
      )}

      {items === null ? (
        <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>Loading observations…</p>
      ) : items.length === 0 ? (
        <div className="py-16 text-center rounded-xl border-2 border-dashed" style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text-soft)" }}>
          Nothing to show yet. {user && <>Be the first — <button className="underline" onClick={() => setShowUpload(true)}>share an observation</button>.</>}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="obs-grid">
          {items.map((o) => <ObservationCard key={o.id} obs={o} categoryLabel={byKey[o.category]?.label} onChanged={load} />)}
        </div>
      )}

      {showUpload && <UploadModal coords={coords} categories={cats} onClose={() => setShowUpload(false)} onSaved={load} />}
    </div>
  );
}


function ObservationCard({ obs, categoryLabel, onChanged }) {
  const { user } = useAuth();
  const mine = user && obs.user_id === user.id;

  const del = async () => {
    if (!window.confirm("Delete this observation?")) return;
    try {
      await http.delete(`/nature/observations/${obs.id}`);
      toast.success("Deleted");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const catLabel = categoryLabel || (obs.category && obs.category !== "other" ? obs.category.replace(/_/g, " ") : null);

  return (
    <article
      data-testid={`obs-card-${obs.id}`}
      className="rounded-xl border overflow-hidden bg-white flex flex-col"
      style={{ borderColor: "var(--wofy-border)" }}
    >
      {obs.image_url && (
        <div className="aspect-[4/3] bg-[var(--wofy-surface)] overflow-hidden relative">
          <img src={obs.image_url} alt={obs.common_name || ""} loading="lazy" className="w-full h-full object-cover" />
          {catLabel && (
            <span
              data-testid={`obs-card-cat-${obs.id}`}
              className="absolute top-2 left-2 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full"
              style={{ background: "rgba(255,255,255,0.92)", color: "var(--wofy-primary)" }}
            >
              {catLabel}
            </span>
          )}
        </div>
      )}
      <div className="p-4 flex-1 flex flex-col">
        <div className="text-[10px] font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>
          {obs.kind || "observation"}
        </div>
        <h3 className="font-semibold mt-0.5">{obs.common_name || "Unidentified"}</h3>
        {obs.scientific_name && (
          <div className="text-xs italic" style={{ color: "var(--wofy-text-soft)" }}>{obs.scientific_name}</div>
        )}
        {obs.notes && <p className="mt-2 text-xs line-clamp-3">{obs.notes}</p>}
        {obs.ai_field_notes && (
          <p
            className="mt-2 text-xs italic leading-relaxed line-clamp-3"
            style={{ color: "var(--wofy-text-soft)" }}
            data-testid={`obs-fieldnotes-${obs.id}`}
          >
            {obs.ai_field_notes}
          </p>
        )}
        <div className="mt-3 text-[11px] font-mono flex items-center gap-2 flex-wrap" style={{ color: "var(--wofy-text-soft)" }}>
          {obs.user_handle ? (
            <Link to={`/u/${obs.user_handle}`} className="hover:underline flex items-center gap-1">
              <User className="w-3 h-3" /> @{obs.user_handle}
            </Link>
          ) : (
            <span>{obs.user_name || "Anon"}</span>
          )}
          <span>·</span>
          <span>{new Date(obs.observed_at || obs.created_at).toLocaleDateString()}</span>
          <span>·</span>
          <a
            href={`https://www.openstreetmap.org/?mlat=${obs.lat}&mlon=${obs.lng}&zoom=13`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 hover:underline"
          >
            <MapPin className="w-3 h-3" /> {obs.location_name || `${obs.lat.toFixed(2)}, ${obs.lng.toFixed(2)}`}
          </a>
        </div>
        {mine && (
          <div className="mt-3 pt-3 border-t flex justify-end" style={{ borderColor: "var(--wofy-border)" }}>
            <button onClick={del} className="wofy-btn-ghost text-xs" data-testid={`obs-delete-${obs.id}`} style={{ color: "#B23A3A" }}>
              <Trash2 className="w-3 h-3" /> Delete
            </button>
          </div>
        )}
      </div>
    </article>
  );
}


function UploadModal({ coords, categories, onClose, onSaved }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [notes, setNotes] = useState("");
  const [locationName, setLocationName] = useState("");
  const [ident, setIdent] = useState(null);
  const [identifying, setIdentifying] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publicVisible, setPublicVisible] = useState(true);
  const [category, setCategory] = useState("");

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 6 * 1024 * 1024) {
      toast.error("Please pick an image under 6 MB.");
      return;
    }
    if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) {
      toast.error("Only JPEG, PNG or WEBP.");
      return;
    }
    setFile(f);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(f);
  };

  const runIdentify = async () => {
    if (!preview) return;
    setIdentifying(true);
    setIdent(null);
    try {
      const b64 = String(preview).split(",", 2)[1];
      const { data } = await http.post("/nature/observations/identify", {
        image_base64: b64, mime_type: file?.type || "image/jpeg",
      });
      setIdent(data);
      // Auto-suggest category if user hasn't chosen one yet.
      if (!category && data?.category && categories.some((c) => c.key === data.category)) {
        setCategory(data.category);
      }
      if (data.not_identifiable) toast.info("Not fully sure — you can label it yourself");
      else toast.success(`Best guess: ${data.common_name || "something interesting"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI identification failed");
    } finally { setIdentifying(false); }
  };

  const save = async () => {
    if (!preview) return toast.error("Please attach a photo.");
    if (!category) return toast.error("Please pick a category.");
    setSaving(true);
    try {
      const b64 = String(preview).split(",", 2)[1];
      let weather_snapshot = null;
      try {
        const w = await http.get(`/nature/weather/current?lat=${coords.lat}&lng=${coords.lng}`);
        weather_snapshot = w.data.current;
      } catch { /* non-fatal */ }
      await http.post("/nature/observations", {
        lat: coords.lat, lng: coords.lng,
        image_base64: b64, mime_type: file?.type || "image/jpeg",
        notes: notes || null,
        location_name: locationName || null,
        common_name: ident?.common_name || null,
        scientific_name: ident?.scientific_name || null,
        family: ident?.family || null,
        kind: ident?.kind || "other",
        category,
        confidence: ident?.confidence || null,
        weather_snapshot,
        public_visible: publicVisible,
      });
      toast.success("Observation saved");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  // Group categories by their "group" label for a nicer dropdown.
  const grouped = useMemo(() => {
    const g = {};
    categories.forEach((c) => { (g[c.group] ||= []).push(c); });
    return g;
  }, [categories]);

  return (
    <div className="fixed inset-0 z-40 bg-[#14213D]/25 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-auto" onClick={onClose} data-testid="obs-upload-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="overline">SHARE AN OBSERVATION</div>
          <button onClick={onClose} className="wofy-btn-ghost text-xs">✕</button>
        </div>

        {preview ? (
          <div className="relative rounded-xl overflow-hidden mb-4">
            <img src={preview} alt="" className="w-full max-h-72 object-cover" />
          </div>
        ) : (
          <label className="block cursor-pointer" data-testid="obs-file-label">
            <div className="rounded-xl border-2 border-dashed py-12 text-center" style={{ borderColor: "var(--wofy-border)" }}>
              <Camera className="w-10 h-10 mx-auto opacity-40" />
              <p className="mt-2 text-sm">Tap to attach a photo</p>
              <p className="text-xs mt-1" style={{ color: "var(--wofy-text-soft)" }}>JPEG / PNG / WEBP · under 6 MB</p>
            </div>
            <input type="file" accept="image/jpeg,image/png,image/webp" onChange={onPick} className="hidden" data-testid="obs-file-input" />
          </label>
        )}

        {preview && (
          <>
            <div className="flex gap-2">
              <button
                data-testid="obs-identify-btn"
                onClick={runIdentify}
                disabled={identifying}
                className="wofy-btn-primary text-sm flex-1 justify-center disabled:opacity-60"
              >
                {identifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {identifying ? "Identifying…" : (ident ? "Re-identify" : "Identify with AI")}
              </button>
              <button className="wofy-btn-ghost text-sm" onClick={() => { setFile(null); setPreview(""); setIdent(null); }}>
                Change photo
              </button>
            </div>

            {ident && (
              <div className="mt-4 p-3 rounded-lg text-sm" style={{ background: "var(--wofy-surface)" }} data-testid="obs-identify-result">
                {ident.not_identifiable ? (
                  <>
                    <b>Not sure —</b>{" "}
                    {ident.guesses?.length ? <>possibly: {ident.guesses.join(", ")}</> : "please add a label below."}
                  </>
                ) : (
                  <>
                    <b>{ident.common_name}</b>{" "}
                    {ident.scientific_name && <span className="italic" style={{ color: "var(--wofy-text-soft)" }}>({ident.scientific_name})</span>}
                    {ident.confidence != null && (
                      <span className="ml-2 text-xs font-mono" style={{ color: "var(--wofy-text-soft)" }}>
                        · {Math.round(ident.confidence * 100)}% confidence
                      </span>
                    )}
                    {ident.description && <p className="mt-1">{ident.description}</p>}
                  </>
                )}
              </div>
            )}

            <div className="mt-4 space-y-3">
              <label className="block">
                <span className="text-xs font-mono uppercase tracking-wider">
                  Category <span style={{ color: "#B23A3A" }}>*</span>
                </span>
                <select
                  data-testid="obs-category-select"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-white"
                  style={{ borderColor: "var(--wofy-border)" }}
                >
                  <option value="">Choose a category…</option>
                  {Object.entries(grouped).map(([group, list]) => (
                    <optgroup key={group} label={group}>
                      {list.map((c) => (
                        <option key={c.key} value={c.key}>{c.label}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                {ident?.category && ident.category !== category && (
                  <button
                    type="button"
                    data-testid="obs-category-suggest"
                    onClick={() => setCategory(ident.category)}
                    className="text-[11px] font-mono mt-1 underline"
                    style={{ color: "var(--wofy-primary)" }}
                  >
                    <Sparkles className="w-3 h-3 inline" /> AI suggests: {categories.find((c) => c.key === ident.category)?.label || ident.category}
                  </button>
                )}
              </label>
              <label className="block">
                <span className="text-xs font-mono uppercase tracking-wider">Location name (optional)</span>
                <input
                  data-testid="obs-location-name"
                  value={locationName}
                  onChange={(e) => setLocationName(e.target.value)}
                  placeholder={`Auto-set to ${coords.lat.toFixed(2)}, ${coords.lng.toFixed(2)}`}
                  className="w-full mt-1 rounded-lg border px-3 py-2 text-sm"
                  style={{ borderColor: "var(--wofy-border)" }}
                />
              </label>
              <label className="block">
                <span className="text-xs font-mono uppercase tracking-wider">Notes</span>
                <textarea
                  data-testid="obs-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  maxLength={1500}
                  placeholder="Habitat, behaviour, anything else you noticed…"
                  className="w-full mt-1 rounded-lg border px-3 py-2 text-sm"
                  style={{ borderColor: "var(--wofy-border)" }}
                />
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="obs-public-visible"
                  checked={publicVisible}
                  onChange={(e) => setPublicVisible(e.target.checked)}
                  className="h-4 w-4 rounded border"
                  style={{ borderColor: "var(--wofy-border)", accentColor: "var(--wofy-primary)" }}
                />
                Show this on the community feed
              </label>
            </div>

            <div className="mt-6 flex gap-2 justify-end">
              <button onClick={onClose} className="wofy-btn-ghost text-sm">Cancel</button>
              <button
                data-testid="obs-save"
                onClick={save}
                disabled={saving}
                className="wofy-btn-primary text-sm disabled:opacity-60"
              >
                {saving ? "Saving…" : "Save observation"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


/* -------------------- Map (OSM iframe fallback) -------------------- */
function MapTab({ coords }) {
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState("");
  const { cats, byKey } = useCategories();
  useEffect(() => {
    const p = new URLSearchParams({ scope: "all", limit: "200" });
    if (category) p.set("category", category);
    http.get(`/nature/observations?${p.toString()}`).then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  }, [category]);
  const bbox = useMemo(() => {
    const d = 5;
    return `${coords.lng - d}%2C${coords.lat - d}%2C${coords.lng + d}%2C${coords.lat + d}`;
  }, [coords.lat, coords.lng]);
  return (
    <div data-testid="map-tab">
      <div className="rounded-xl overflow-hidden border" style={{ borderColor: "var(--wofy-border)" }}>
        <iframe
          title="observations-map"
          src={`https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${coords.lat}%2C${coords.lng}`}
          className="w-full h-[480px] border-0"
        />
      </div>
      <p className="text-xs mt-2" style={{ color: "var(--wofy-text-soft)" }}>
        Showing your area. {items.length} recent observation{items.length !== 1 ? "s" : ""} community-wide — pin markers coming soon.
      </p>

      {/* Category filter chips for the map thumbnails */}
      {cats.length > 0 && (
        <div className="mt-5 flex flex-wrap gap-1.5" data-testid="map-category-chips">
          <button
            onClick={() => setCategory("")}
            className={`text-[11px] px-3 py-1 rounded-full border ${category === "" ? "bg-[var(--wofy-primary)] text-white border-transparent" : ""}`}
            style={category !== "" ? { borderColor: "var(--wofy-border)" } : undefined}
          >
            All
          </button>
          {cats.map((c) => (
            <button
              key={c.key}
              data-testid={`map-category-chip-${c.key}`}
              onClick={() => setCategory(category === c.key ? "" : c.key)}
              className={`text-[11px] px-3 py-1 rounded-full border ${category === c.key ? "bg-[var(--wofy-primary)] text-white border-transparent" : ""}`}
              style={category !== c.key ? { borderColor: "var(--wofy-border)" } : undefined}
            >
              {c.label}
            </button>
          ))}
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        {items.slice(0, 12).map((o) => <ObservationCard key={o.id} obs={o} categoryLabel={byKey[o.category]?.label} onChanged={() => {}} />)}
      </div>
    </div>
  );
}


/* -------------------- My Journal -------------------- */
function JournalTab() {
  const { user } = useAuth();
  const { byKey } = useCategories();
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (!user) return;
    http.get(`/nature/observations?scope=mine&limit=200`).then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  }, [user]);
  if (!user) return (
    <div className="py-16 text-center rounded-xl border-2 border-dashed" style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text-soft)" }}>
      Sign in to keep your personal nature journal. <Link to="/login" className="underline">Sign in</Link>
    </div>
  );
  if (items === null) return <p className="text-sm">Loading…</p>;
  if (items.length === 0) return (
    <div className="py-16 text-center rounded-xl border-2 border-dashed" style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text-soft)" }}>
      Your journal is empty. Go to Observations → Share an observation to start.
    </div>
  );
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="journal-grid">
      {items.map((o) => <ObservationCard key={o.id} obs={o} categoryLabel={byKey[o.category]?.label} onChanged={() => {}} />)}
    </div>
  );
}


/* -------------------- Leaderboard -------------------- */
function LeaderboardTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    http.get(`/nature/leaderboard?days=7`).then((r) => setData(r.data)).catch(() => setData({ top_species: [], top_observers: [] }));
  }, []);
  if (!data) return <p className="text-sm">Loading…</p>;
  return (
    <div className="grid md:grid-cols-2 gap-6" data-testid="leaderboard-tab">
      <section className="wofy-card">
        <div className="overline">TOP SPECIES · LAST 7 DAYS</div>
        {data.top_species.length === 0 ? (
          <p className="mt-3 text-sm" style={{ color: "var(--wofy-text-soft)" }}>No species spotted yet.</p>
        ) : (
          <ol className="mt-4 space-y-3">
            {data.top_species.map((s, i) => (
              <li key={`${s.common_name}-${i}`} className="flex items-center gap-3" data-testid={`species-row-${i}`}>
                <div className="w-8 text-xs font-mono opacity-60">#{i + 1}</div>
                {s.cover ? <img src={s.cover} alt="" className="w-12 h-12 rounded-lg object-cover" /> : <div className="w-12 h-12 rounded-lg" style={{ background: "var(--wofy-surface)" }} />}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{s.common_name}</div>
                  <div className="text-[11px] italic truncate" style={{ color: "var(--wofy-text-soft)" }}>{s.scientific_name}</div>
                </div>
                <div className="font-display text-xl">{s.count}</div>
              </li>
            ))}
          </ol>
        )}
      </section>

      <section className="wofy-card">
        <div className="overline">TOP OBSERVERS · LAST 7 DAYS</div>
        {data.top_observers.length === 0 ? (
          <p className="mt-3 text-sm" style={{ color: "var(--wofy-text-soft)" }}>No observers yet.</p>
        ) : (
          <ol className="mt-4 space-y-3">
            {data.top_observers.map((p, i) => (
              <li key={p.user_id} className="flex items-center gap-3" data-testid={`observer-row-${p.user_id}`}>
                <div className="w-8 text-xs font-mono opacity-60">#{i + 1}</div>
                {p.avatar ? <img src={p.avatar} alt="" className="w-10 h-10 rounded-full object-cover" /> : <div className="w-10 h-10 rounded-full flex items-center justify-center font-display" style={{ background: "var(--wofy-surface)" }}>{(p.name || "?").slice(0, 1).toUpperCase()}</div>}
                <div className="flex-1 min-w-0">
                  {p.handle ? (
                    <Link to={`/u/${p.handle}`} className="font-semibold text-sm hover:underline">{p.name || "Someone"}</Link>
                  ) : <div className="font-semibold text-sm">{p.name || "Someone"}</div>}
                  <div className="text-[11px] font-mono" style={{ color: "var(--wofy-text-soft)" }}>
                    {p.count} observation{p.count !== 1 ? "s" : ""} · {p.unique_species} species
                  </div>
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}


/* -------------------- Alerts -------------------- */
function AlertsTab({ coords }) {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    http.get("/nature/alerts/preferences").then((r) => setPrefs(r.data)).catch(() => setPrefs({ enabled: false }));
  }, [user]);

  const loadAlerts = useCallback(() => {
    if (!user) return;
    http.get("/nature/alerts").then((r) => setAlerts(r.data.alerts || [])).catch(() => setAlerts([]));
  }, [user]);

  useEffect(() => { if (prefs?.enabled) loadAlerts(); }, [prefs?.enabled, loadAlerts]);

  const save = async (enabled) => {
    setBusy(true);
    try {
      const payload = { enabled, lat: coords.lat, lng: coords.lng, location_name: null };
      const { data } = await http.post("/nature/alerts/preferences", payload);
      setPrefs(data);
      toast.success(enabled ? "Alerts on — we'll email you severe weather updates" : "Alerts off");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  if (!user) return (
    <div className="py-16 text-center rounded-xl border-2 border-dashed" style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text-soft)" }}>
      Sign in to enable weather alerts. <Link to="/login" className="underline">Sign in</Link>
    </div>
  );
  if (!prefs) return <p className="text-sm">Loading…</p>;

  return (
    <div className="max-w-2xl" data-testid="alerts-tab">
      <div className="wofy-card">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="overline">WEATHER ALERTS</div>
            <h3 className="font-semibold mt-1">Get warned about storms & severe weather.</h3>
            <p className="text-sm mt-1" style={{ color: "var(--wofy-text-soft)" }}>
              Uses your current location ({coords.lat.toFixed(2)}, {coords.lng.toFixed(2)}). We poll WeatherAPI for active alerts.
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer whitespace-nowrap">
            <input
              type="checkbox"
              data-testid="alerts-toggle"
              checked={!!prefs.enabled}
              disabled={busy}
              onChange={(e) => save(e.target.checked)}
              className="h-4 w-4 rounded border"
              style={{ borderColor: "var(--wofy-border)", accentColor: "var(--wofy-primary)" }}
            />
            <span>{prefs.enabled ? "On" : "Off"}</span>
          </label>
        </div>
      </div>

      {prefs.enabled && (
        <div className="mt-6" data-testid="alerts-list">
          <div className="overline mb-3">ACTIVE ALERTS</div>
          {alerts === null ? (
            <p className="text-sm">Loading…</p>
          ) : alerts.length === 0 ? (
            <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>No active alerts. Skies are calm.</p>
          ) : (
            <ul className="space-y-3">
              {alerts.map((a, i) => (
                <li key={i} className="rounded-lg border p-4" style={{ borderColor: "var(--wofy-border)" }} data-testid={`alert-item-${i}`}>
                  <div className="text-xs font-mono uppercase tracking-wider" style={{ color: "#B23A3A" }}>{a.severity || "advisory"}</div>
                  <div className="font-semibold">{a.event}</div>
                  <div className="text-xs mt-1" style={{ color: "var(--wofy-text-soft)" }}>{a.areas}</div>
                  <p className="text-sm mt-2 line-clamp-3">{a.headline || a.desc}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
